// Client-side data loading from static files
import pako from "pako";

// Base URL for data files (artwork blocks, emotions, mini index).
// Set VITE_DATA_URL to serve from R2/CDN; defaults to same origin.
const DATA_URL = (import.meta.env.VITE_DATA_URL as string) || "";

// Lazily-loaded `versions.json` from R2 root: maps each path (relative to
// DATA_URL) to a content hash. Used to cache-bust block fetches via `?v=`.
// versions.json itself is served with `Cache-Control: no-cache` so the
// browser revalidates it (304 when unchanged) without a hard refresh.
let versionsPromise: Promise<Record<string, string>> | null = null;
function loadVersions(): Promise<Record<string, string>> {
  if (!versionsPromise) {
    versionsPromise = fetch(`${DATA_URL}/versions.json`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return versionsPromise;
}
export async function versionedUrl(path: string): Promise<string> {
  const versions = await loadVersions();
  const v = versions[path];
  return v ? `${DATA_URL}/${path}?v=${v}` : `${DATA_URL}/${path}`;
}

const MAX_ARTWORK_ID = 13511;
export interface StaticArtwork {
  title: string;
  id?: number | null;
  artist: string | null;
  year?: string;
  movement?: string;
  url: string | null;
  description?: string;
  wikiUrl?: string;
}

let cachedArtworks: StaticArtwork[] | null = null;
let cachedArtworkBlocks: Map<string, any[]> = new Map();

const loadGzippedArtworks = async (path: string) => {
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`Failed to load ${path}: ${response.statusText}`);
    }

    // Get the response as an ArrayBuffer
    const gzippedBuffer = await response.arrayBuffer();

    // Create a Uint8Array from the ArrayBuffer
    const uint8Array = new Uint8Array(gzippedBuffer);

    // Check if the file is actually compressed (gzip magic number: 1f 8b)
    const isGzipped = uint8Array[0] === 0x1f && uint8Array[1] === 0x8b;

    let decompressed;
    if (isGzipped) {
      // File is actually gzipped, decompress it
      console.log(`Loading gzipped file: ${path}`);
      try {
        decompressed = pako.ungzip(uint8Array, { to: "string" });
      } catch (pakoError) {
        console.error("Pako decompression error:", pakoError);
        throw new Error(`Decompression failed: ${pakoError}`);
      }
    } else {
      // File is not gzipped, treat as plain text
      console.log(`Loading uncompressed file: ${path}`);
      decompressed = new TextDecoder().decode(uint8Array);
    }

    // Parse the decompressed string as JSON
    const artworksData = JSON.parse(decompressed);

    console.log(`Loaded ${artworksData.length} artworks from ${path}`);
    return artworksData;
  } catch (error) {
    console.error("Error loading artworks:", error);
    throw error;
  }
};

export async function loadArtworkById(id: number): Promise<any | null> {
  try {
    // Determine which compressed file to load from
    const blockStart = Math.floor(id / 10000) * 10000;
    const blockEnd = blockStart + 10000;
    const filename = `${blockStart}-${blockEnd}.json.gz`;

    // Check if this block is already cached
    if (cachedArtworkBlocks.has(filename)) {
      const artworks = cachedArtworkBlocks.get(filename);
      const artwork = artworks?.find((art: any) => art.id === id);
      return artwork || null;
    }

    // Load and decompress the appropriate file
    console.log(`Loading compressed artwork file: ${filename}`);
    const artworks = await loadGzippedArtworks(await versionedUrl(`artworks/${filename}`));

    console.log(`Loaded ${artworks.length} artworks from ${filename}`);

    // Cache the decompressed block for future use
    cachedArtworkBlocks.set(filename, artworks);

    // Find the specific artwork
    const artwork = artworks.find((art: any) => art.id === id);
    if (artwork) {
      console.log(`Found artwork ${id} in ${filename}`);
    } else {
      console.warn(`Artwork ${id} not found in ${filename}`);
    }
    return artwork || null;
  } catch (error) {
    console.error(`Error loading artwork ${id}:`, error);
    return null;
  }
}

export async function loadAllArtworks(): Promise<any[]> {
  if (cachedArtworks) return cachedArtworks;

  try {
    // Try to load the comprehensive artworks database, fallback to uncompressed version
    let artworksData;
    try {
      artworksData = await loadGzippedArtworks(await versionedUrl("artworksMini.json.gz"));
    } catch (error) {
      console.warn("Failed to load compressed version, trying uncompressed...");
      artworksData = await loadGzippedArtworks(`${DATA_URL}/artworksMini.json`);
    }

    const titleEntries = artworksData
      // .filter((x) => x.id <= MAX_ARTWORK_ID)
      .map(
        ({
          id,
          url,
          title,
          imageUrl,
          artist,
        }: {
          id: number;
          url?: string;
          title: string;
          imageUrl?: string;
          artist?: string;
        }) => ({
          id: id,
          title: title,
          artist: artist || "Unknown Artist",
          year: "",
          movement: "",
          url: imageUrl || url,
          imageUrl: imageUrl || url,
          wikiUrl: "",
        }),
      );

    cachedArtworks = titleEntries;
    console.log(
      `Loaded ${cachedArtworks?.length} artworks from comprehensive database`,
    );

    return cachedArtworks || [];
  } catch (error) {
    console.error("Failed to load comprehensive artworks database:", error);
  }

  // Return empty array if no data available
  cachedArtworks = [];
  console.log('No artwork data available, returning empty array');
  return cachedArtworks;
}

export async function loadComprehensiveArtworks(): Promise<StaticArtwork[]> {
  if (cachedArtworks) return cachedArtworks;

  // Use all artworks as the comprehensive search dataset
  const artworks = await loadAllArtworks();
  return artworks || [];
}

export function getRandomArtwork(artworks: any[]): any | null {
  if (!artworks || artworks.length === 0) return null;
  return artworks[Math.floor(Math.random() * artworks.length)];
}

export function searchArtworks(
  artworks: StaticArtwork[],
  query: string,
  limit: number = 10,
): StaticArtwork[] {
  if (!query || !artworks) return [];

  const lowercaseQuery = query.toLowerCase();
  const results: StaticArtwork[] = [];

  for (const artwork of artworks) {
    if (results.length >= limit) break;

    const titleMatch = artwork.title?.toLowerCase().includes(lowercaseQuery);
    const artistMatch = artwork.artist?.toLowerCase().includes(lowercaseQuery);
    const movementMatch = artwork.movement
      ?.toLowerCase()
      .includes(lowercaseQuery);

    if (titleMatch || artistMatch || movementMatch) {
      results.push(artwork);
    }
  }

  return results;
}

// Cached emotion data
let cachedEmotionData: Map<number, any[]> | null = null;
const emotionBlockCache = new Map<string, Map<number, EmotionData[]>>();

export interface EmotionData {
  emotion: string;
  value: number;
}

async function loadEmotionBlock(blockStart: number): Promise<Map<number, EmotionData[]>> {
  const blockEnd = blockStart + 9999;
  const url = await versionedUrl(`emotions/${blockStart}-${blockEnd}.json.gz`);
  if (!emotionBlockCache.has(url)) {
    const data = await loadGzippedArtworks(url);
    const blockMap = new Map<number, EmotionData[]>();
    Object.entries(data).forEach(([id, scoreObj]: [string, any]) => {
      const emotions: EmotionData[] = Object.entries(scoreObj).map(
        ([emotion, value]) => ({ emotion, value: value as number })
      );
      blockMap.set(parseInt(id), emotions);
    });
    emotionBlockCache.set(url, blockMap);
  }
  return emotionBlockCache.get(url)!;
}

export const loadEmotionData = async (): Promise<Map<number, EmotionData[]>> => {
  if (cachedEmotionData) return cachedEmotionData;
  try {
    const manifest: number[] = await fetch(await versionedUrl("emotions/manifest.json")).then(r => r.json());
    const blocks = await Promise.all(manifest.map(loadEmotionBlock));
    const merged = new Map<number, EmotionData[]>();
    for (const block of blocks) {
      for (const [id, emotions] of block) merged.set(id, emotions);
    }
    cachedEmotionData = merged;
    console.log(`Loaded emotion data for ${merged.size} artworks`);
    return merged;
  } catch (error) {
    console.warn('Error loading emotion data:', error);
    cachedEmotionData = new Map();
    return cachedEmotionData;
  }
};
